/**
 * FormulaInput — rich contentEditable formula editor with @-mention event picker.
 *
 * Single unified editing surface (no tiny input boxes). Event names inserted
 * via `@` render as styled inline chips. Clicking a chip reopens the picker
 * to replace it; Backspace naturally deletes chips.
 *
 * The dropdown is rendered via portal to escape parent overflow clipping.
 */
import { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Search } from 'lucide-react';
import { cn } from '@/lib/cn';

// ─── Props ──────────────────────────────────────────────────────────────────

interface FormulaInputProps {
  value: string;
  onChange: (formula: string) => void;
  /** All available event names for the @ picker */
  events: string[];
  placeholder?: string;
}

// ─── Chip helpers ───────────────────────────────────────────────────────────

const CHIP_CLASS = 'fi-chip';

const CHIP_STYLE = [
  'display:inline-flex',
  'align-items:center',
  'height:22px',
  'padding:0 8px',
  'margin:1px 2px',
  'border-radius:6px',
  'background:#e0e7ff',
  'color:#4338ca',
  'font-size:12px',
  'font-weight:600',
  'font-family:ui-monospace,SFMono-Regular,"SF Mono",Menlo,monospace',
  'border:1px solid #c7d2fe',
  'cursor:pointer',
  'user-select:none',
  'vertical-align:middle',
  'line-height:20px',
  'box-shadow:0 1px 2px rgba(0,0,0,0.04)',
].join(';');

function esc(text: string): string {
  return text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

function chipHtml(name: string): string {
  return `<span class="${CHIP_CLASS}" data-event="${esc(name)}" contenteditable="false" style="${CHIP_STYLE}">${esc(name)}</span>`;
}

/** Walk the editor DOM and reconstruct the formula string. */
function domToFormula(container: HTMLElement): string {
  let out = '';
  const walk = (node: Node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      out += node.textContent || '';
    } else if (node instanceof HTMLElement) {
      if (node.dataset.event) {
        out += node.dataset.event;
      } else {
        node.childNodes.forEach(walk);
      }
    }
  };
  container.childNodes.forEach(walk);
  // Strip zero-width and non-breaking spaces added by browsers
  return out.replace(/[\u200B\u200C\u200D\uFEFF]/g, '');
}

/** Convert a formula string → innerHTML with chip spans for known events. */
function formulaToHtml(formula: string, events: string[]): string {
  if (!formula) return '';
  if (events.length === 0) return esc(formula);

  const sorted = [...events].sort((a, b) => b.length - a.length);
  const escaped = sorted.map((e) => e.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
  const pattern = new RegExp(`\\b(${escaped.join('|')})\\b`, 'g');

  let html = '';
  let last = 0;
  let m: RegExpExecArray | null;
  while ((m = pattern.exec(formula)) !== null) {
    if (m.index > last) html += esc(formula.slice(last, m.index));
    html += chipHtml(m[1]);
    last = m.index + m[0].length;
  }
  if (last < formula.length) html += esc(formula.slice(last));
  return html;
}

// ─── Dropdown state ─────────────────────────────────────────────────────────

interface DDState {
  open: boolean;
  filter: string;
  highlight: number;
  mode: 'insert' | 'replace';
}
const DD_CLOSED: DDState = { open: false, filter: '', highlight: 0, mode: 'insert' };

// ─── Component ──────────────────────────────────────────────────────────────

export function FormulaInput({ value, onChange, events, placeholder }: FormulaInputProps) {
  const editorRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  /** Range saved when @ is pressed (before dropdown steals focus). */
  const savedRange = useRef<Range | null>(null);
  /** Chip element being replaced. */
  const replacingChip = useRef<HTMLElement | null>(null);
  /** Suppress external sync after internal edits. */
  const internalValue = useRef(value);

  const [dd, setDd] = useState<DDState>(DD_CLOSED);
  const [portalPos, setPortalPos] = useState({ top: 0, left: 0, width: 0 });
  const [empty, setEmpty] = useState(!value);

  // ── Initial mount: render formula as HTML with chips ───────────────────
  const didMount = useRef(false);
  useEffect(() => {
    if (!editorRef.current) return;
    if (!didMount.current) {
      // First render — always paint even when internalValue matches
      didMount.current = true;
      if (value) {
        editorRef.current.innerHTML = formulaToHtml(value, events);
        setEmpty(false);
      }
      return;
    }
    // Subsequent renders — only sync when changed from outside
    if (internalValue.current === value) return;
    editorRef.current.innerHTML = formulaToHtml(value, events);
    internalValue.current = value;
    setEmpty(!value);
  }, [value, events]);

  // ── Extract formula from DOM and push upstream ────────────────────────
  const sync = useCallback(() => {
    if (!editorRef.current) return;
    const f = domToFormula(editorRef.current);
    internalValue.current = f;
    setEmpty(!f.trim());
    onChange(f);
  }, [onChange]);

  // ── Input handler ─────────────────────────────────────────────────────
  const handleInput = useCallback(() => sync(), [sync]);

  // ── Paste as plain text ───────────────────────────────────────────────
  const handlePaste = useCallback((e: React.ClipboardEvent) => {
    e.preventDefault();
    const text = e.clipboardData.getData('text/plain');
    document.execCommand('insertText', false, text);
  }, []);

  // ── @ trigger ─────────────────────────────────────────────────────────
  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === '@') {
      e.preventDefault();
      const sel = window.getSelection();
      if (sel && sel.rangeCount > 0) {
        savedRange.current = sel.getRangeAt(0).cloneRange();
      }
      replacingChip.current = null;
      setDd({ open: true, filter: '', highlight: 0, mode: 'insert' });
    }
  }, []);

  // ── Click on chip → replace ───────────────────────────────────────────
  const handleEditorClick = useCallback((e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    const chip = target.classList.contains(CHIP_CLASS)
      ? target
      : (target.closest(`.${CHIP_CLASS}`) as HTMLElement | null);
    if (chip?.dataset.event) {
      e.preventDefault();
      replacingChip.current = chip;
      setDd({ open: true, filter: '', highlight: 0, mode: 'replace' });
    }
  }, []);

  // ── Filtered events ───────────────────────────────────────────────────
  const filteredEvents = useMemo(() => {
    if (!dd.filter) return events;
    const f = dd.filter.toLowerCase();
    return events.filter((ev) => ev.toLowerCase().includes(f));
  }, [events, dd.filter]);

  // Reset highlight when filter changes
  useEffect(() => {
    setDd((p) => (p.open ? { ...p, highlight: 0 } : p));
  }, [dd.filter]);

  // Focus search on open
  useEffect(() => {
    if (dd.open) requestAnimationFrame(() => searchRef.current?.focus());
  }, [dd.open]);

  // ── Select event from dropdown ────────────────────────────────────────
  const selectEvent = useCallback(
    (eventName: string) => {
      const editor = editorRef.current;
      if (!editor) {
        setDd(DD_CLOSED);
        return;
      }

      if (dd.mode === 'replace' && replacingChip.current) {
        // Replace existing chip
        const chip = replacingChip.current;
        chip.dataset.event = eventName;
        chip.textContent = eventName;
        replacingChip.current = null;
      } else {
        // Insert at saved cursor position
        editor.focus();
        const sel = window.getSelection();
        if (savedRange.current && sel) {
          sel.removeAllRanges();
          sel.addRange(savedRange.current);
        }

        const chip = document.createElement('span');
        chip.className = CHIP_CLASS;
        chip.dataset.event = eventName;
        chip.contentEditable = 'false';
        chip.setAttribute('style', CHIP_STYLE);
        chip.textContent = eventName;

        if (sel && sel.rangeCount > 0) {
          const range = sel.getRangeAt(0);
          range.deleteContents();
          range.insertNode(chip);

          // Add a thin space after chip so the cursor can land there
          const spacer = document.createTextNode(' ');
          chip.after(spacer);
          range.setStartAfter(spacer);
          range.setEndAfter(spacer);
          sel.removeAllRanges();
          sel.addRange(range);
        }

        savedRange.current = null;
      }

      sync();
      setDd(DD_CLOSED);
      requestAnimationFrame(() => editor.focus());
    },
    [dd.mode, sync],
  );

  // ── Dropdown keyboard navigation ──────────────────────────────────────
  const handleSearchKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        setDd(DD_CLOSED);
        requestAnimationFrame(() => editorRef.current?.focus());
        return;
      }
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setDd((p) => ({ ...p, highlight: Math.min(p.highlight + 1, filteredEvents.length - 1) }));
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setDd((p) => ({ ...p, highlight: Math.max(p.highlight - 1, 0) }));
        return;
      }
      if (e.key === 'Enter') {
        e.preventDefault();
        if (filteredEvents[dd.highlight]) selectEvent(filteredEvents[dd.highlight]);
      }
    },
    [filteredEvents, dd.highlight, selectEvent],
  );

  // ── Portal positioning ────────────────────────────────────────────────
  useEffect(() => {
    if (!dd.open || !containerRef.current) return;
    const update = () => {
      const rect = containerRef.current!.getBoundingClientRect();
      setPortalPos({
        top: rect.bottom + 4,
        left: rect.left,
        width: Math.max(rect.width, 288),
      });
    };
    update();
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [dd.open]);

  // ── Scroll highlighted item into view ─────────────────────────────────
  useEffect(() => {
    if (!dd.open) return;
    const el = dropdownRef.current?.querySelector(`[data-idx="${dd.highlight}"]`);
    el?.scrollIntoView({ block: 'nearest' });
  }, [dd.open, dd.highlight]);

  // ── Outside click to close dropdown ───────────────────────────────────
  useEffect(() => {
    if (!dd.open) return;
    const handler = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node) &&
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setDd(DD_CLOSED);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [dd.open]);

  // ─────────────────────────────────────────────────────────────────────
  return (
    <div ref={containerRef}>
      {/* ── Editor surface ──────────────────────────────────────────────── */}
      <div className="relative">
        <div
          ref={editorRef}
          contentEditable
          suppressContentEditableWarning
          onInput={handleInput}
          onKeyDown={handleKeyDown}
          onClick={handleEditorClick}
          onPaste={handlePaste}
          className={cn(
            'w-full min-h-[100px] max-h-[280px] overflow-y-auto',
            'px-3.5 py-3 rounded-lg border bg-white',
            'text-sm font-mono text-slate-900 leading-7',
            'transition-colors duration-150',
            'focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-400',
            'border-slate-200 hover:border-slate-300',
          )}
          style={{ wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}
        />
        {/* Placeholder */}
        {empty && (
          <div
            className="absolute top-3 left-3.5 text-sm font-mono text-slate-400 pointer-events-none select-none leading-7"
            aria-hidden
          >
            {placeholder}
          </div>
        )}
      </div>

      {/* ── Hint ────────────────────────────────────────────────────────── */}
      <p className="text-[10px] text-slate-400 mt-1.5">
        Type{' '}
        <kbd className="px-1 py-0.5 rounded bg-slate-100 text-slate-600 font-mono text-[10px]">
          @
        </kbd>{' '}
        to insert an event. Operators: + - * / ( )
      </p>

      {/* ── Event picker dropdown (portal) ──────────────────────────────── */}
      {dd.open &&
        createPortal(
          <>
            <div className="fixed inset-0 z-[9998]" onClick={() => setDd(DD_CLOSED)} />
            <div
              ref={dropdownRef}
              className="fixed z-[9999] bg-white border border-slate-200 rounded-xl shadow-xl overflow-hidden"
              style={{
                top: portalPos.top,
                left: portalPos.left,
                width: portalPos.width,
                maxWidth: 'min(400px, 90vw)',
              }}
            >
              {/* Search */}
              <div className="p-2 border-b border-slate-100">
                <div className="relative">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                  <input
                    ref={searchRef}
                    type="text"
                    value={dd.filter}
                    onChange={(e) => setDd((p) => ({ ...p, filter: e.target.value }))}
                    onKeyDown={handleSearchKeyDown}
                    placeholder="Search events..."
                    className="w-full pl-8 pr-3 py-1.5 text-sm rounded-lg border border-slate-200 bg-slate-50 focus:outline-none focus:ring-1 focus:ring-brand-400 focus:border-brand-400 focus:bg-white transition-colors"
                  />
                </div>
              </div>

              {/* Event list */}
              <div className="max-h-52 overflow-y-auto py-1">
                {filteredEvents.length === 0 ? (
                  <div className="px-3 py-6 text-center text-xs text-slate-400">
                    No events found
                  </div>
                ) : (
                  filteredEvents.map((event, idx) => (
                    <button
                      key={event}
                      type="button"
                      data-idx={idx}
                      onClick={() => selectEvent(event)}
                      className={cn(
                        'w-full px-3 py-2 text-left text-sm font-mono transition-colors',
                        idx === dd.highlight
                          ? 'bg-brand-50 text-brand-700'
                          : 'text-slate-700 hover:bg-slate-50',
                      )}
                    >
                      {event}
                    </button>
                  ))
                )}
              </div>
            </div>
          </>,
          document.body,
        )}
    </div>
  );
}
